import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { CategoryType } from '@core/models/category-type.model';
import { ContractCgrpOff } from '@core/models/contract-cgrp-off.model';
import { ContractItem } from '@core/models/contract-item.model';
import { Contract } from '@core/models/contract.model';
import { FilteredItem } from '@core/models/filtered-item.model';
import { NegotiationType } from '@core/models/negotiation-type.model';
import { FilteredTarget, Target } from '@core/models/target.model';
import { CategoryTypeService } from '@core/services/category-type/category-type.service';
import { ContractCategoryTypeService } from '@core/services/contract-category-type/contract-category-type.service';
import { ContractCgrpOffService } from '@core/services/contract-cgrp-off/contract-cgrp-off.service';
import { ContractCgvModelUpdateService } from '@core/services/contract-cgv-model-update/contract-cgv-model-update.service';
import { TargetService } from '@core/services/target.service';
import { TvSelectItemsDialogComponent } from '@shared/dialog/tv-select-items-dialog/tv-select-items-dialog.component';
import { Observable, of, Subject } from 'rxjs';
import { finalize, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-cgrp-off-managing-dialog',
  templateUrl: './cgrp-off-managing-dialog.component.html',
  styleUrls: ['./cgrp-off-managing-dialog.component.scss'],
  standalone: false,
})
export class CGRPOffManagingDialog implements OnInit, OnDestroy {
  noCategoryBlockAdded: boolean = true;
  categoryConditionType: string = null;
  categories = [
    { value: 'Product', viewValue: 'Produit (Pr)' },
    { value: 'Advertiser', viewValue: 'Annonceur (An)' },
    { value: 'Specific', viewValue: 'Specific (Sp)' },
  ];
  @Input() cgrpOffData: any;
  @Output() readonly saveStarted = new EventEmitter<void>();
  public filteredItemList: Observable<any[]>;
  public filteredOptions: Observable<any[]>;
  public itemList: any[] = [];
  itemsFilterForm: FormGroup;
  targetControl = new FormControl(null, [Validators.required]);
  budgetControl = new FormControl(null, [Validators.required]);
  categoryTypeFormControl = new FormControl(null);
  searchFormControl = new FormControl(null);
  optionFormControl = new FormControl(null);
  filter = [];
  isCategoryTypeLoading: boolean = false;
  isTargetLoading: boolean = true;
  isTargetLoaded: boolean = false;
  isItemLoading: boolean = false;
  categoryTypeList: CategoryType[];
  targetList: Target[];
  filteredTargetList: Observable<Target[]>;
  componentDestroyed$: Subject<void> = new Subject();
  advertiserId = null;
  contractYear = null;
  public choosenItemList: any[] = [];
  public editStartDate = '';
  public editEndDate = '';
  public isEditing = false;
  public idDuplicating = false;
  public isShowOption = false;
  public selectedOptions: Set<any> = new Set();
  public conditionRows: NonNullable<ContractCgrpOff['conditions']> = [];
  readonly conditionColumns = ['category', 'name', 'number', 'remove'];
  public selectAllOptions = false;
  public saving = false;
  public categoryType = null;
  public isSelectItemsDialogOpen = false;
  private readonly targetAutocompleteLimit = 100;
  private readonly itemAutocompleteLimit = 100;
  private loadingCategoryTypeId: number | null = null;

  @ViewChild('itemAutocompleteTrigger', { read: MatAutocompleteTrigger })
  private itemAutocompleteTrigger?: MatAutocompleteTrigger;

  get conditionCategoryCount(): number {
    return new Set(this.conditionRows.map(row => row.categoryType?.id).filter(id => id != null)).size;
  }

  get isLoading(): boolean {
    return this.isTargetLoading || this.isCategoryTypeLoading;
  }

  constructor(
    private iconRegistry: MatIconRegistry,
    private fb: FormBuilder,
    private targetService: TargetService,
    private categoryTypeService: CategoryTypeService,
    private contractCategoryTypeService: ContractCategoryTypeService,
    private contractCgrpOffService: ContractCgrpOffService,
    private contractCgvModelUpdateService: ContractCgvModelUpdateService,
    private sanitizer: DomSanitizer,
    public dialogRef: MatDialogRef<CGRPOffManagingDialog>,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      contract: Contract;
      contractItem: ContractItem;
      cgrpOffData: ContractCgrpOff;
      action: string;
    }
  ) {
    iconRegistry.addSvgIcon('add-circle-fill', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/add-circle-fill.svg'));
    iconRegistry.addSvgIcon('delete-circle-fill', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/delete-circle-fill.svg'));
  }

  ngOnInit(): void {
    this.itemsFilterForm = this.fb.group({
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      search: [null, []],
    });

    this.getTargetList('');
    this.getCategoryTypeList();

    this.handleCategoryTypeValueChange();
    this.handleItemValueChange();

    if (this.data) {
      this.advertiserId = this.data.contract.advertiserId;
      this.contractYear = this.data.contract.stateContractYear;

      this.initFormData();
    }
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }

  private initFormData() {
    if (this.data.cgrpOffData) {
      if (this.data.action == 'edit') {
        this.idDuplicating = true;
      } else {
        this.isEditing = true;
      }

      this.budgetControl.setValue(this.data.cgrpOffData.value);

      // pre select for date
      // const from = moment(this.data.cgrpOffData.startDate, 'DD/MM/YYYY');
      // const to = moment(this.data.cgrpOffData.endDate, 'DD/MM/YYYY');
      this.editStartDate = this.data.cgrpOffData.startDate;
      this.editEndDate = this.data.cgrpOffData.endDate;

      if (this.data.cgrpOffData.conditions.length > 0) {
        if (this.data.cgrpOffData.conditions[0].isInclude) {
          this.addCategoryBloc('Inclus');
        } else {
          this.addCategoryBloc('Exclut');
        }

        this.selectedOptions = new Set();
        let conditions = this.data.cgrpOffData.conditions;
        for (let i = 0; i < conditions.length; i++) {
          this.selectedOptions.add(conditions[i]);
        }

        this.sortSelectedOptions();
      }
    } else {
      this.editStartDate = `01/01/${this.data.contract.cgvContractYear}`;
      this.editEndDate = `31/12/${this.data.contract.cgvContractYear}`;
    }

    this.itemsFilterForm.patchValue({
      startDate: this.editStartDate,
      endDate: this.editEndDate,
    });
  }

  private handleItemValueChange() {
    this.searchFormControl.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      if (typeof value !== 'string' && value) {
        if (!this.isOptionSelected(value)) {
          this.selectedOptions.add({
            categoryType: this.categoryTypeFormControl.value,
            isInclude: this.categoryConditionType == 'Inclus' ? 1 : 0,
            category: value,
          });

          this.sortSelectedOptions();
        }

        this.searchFormControl.setValue('', { emitEvent: false });
      }
    });
  }

  removeItem(chip: string): void {
    const index = this.choosenItemList.indexOf(chip);
    if (index >= 0) {
      this.choosenItemList.splice(index, 1);
    }
  }

  public getFormValues() {
    return {
      // isInclude: this.conditionType,
      // categoryType: this.categoryTypeFormControl.value,
      choosenItemList: this.choosenItemList,
    };
  }

  private getTargetList(name) {
    this.isTargetLoading = true;
    const filter = {
      name: name,
    };

    this.targetService
      .getList(filter)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        targetList => {
          this.isTargetLoading = false;
          this.isTargetLoaded = true;
          this.targetList = targetList;
          if (this.data.cgrpOffData) {
            const preselectedItem = this.targetList.find(item => item.id === this.data.cgrpOffData.targetCode);
            if (preselectedItem) {
              this.targetControl.setValue(preselectedItem);
            }
          }
          this.initFilteredTargetList();
        },
        error => {
          this.isTargetLoading = false;
          this.isTargetLoaded = true;
        }
      );
  }

  private initFilteredTargetList() {
    if (!this.isTargetLoading) {
      this.filteredTargetList = this.targetControl.valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => {
          return this._getLabel(value);
        }),
        map(label => this.getTargetAutocompleteOptions(label))
      );
    }
  }

  private _getLabel(value: string | Target): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  /***
   * Fonction pour afficher le label dans l'input lors de la sélection
   * @param value
   * @private
   */
  private _filter(value: string = ''): Target[] {
    const filterValue = value.toLowerCase();
    return this.targetList
      .filter(target => target.id?.toString().toLowerCase().includes(filterValue) || target.name?.toLowerCase().includes(filterValue))
      .slice(0, this.targetAutocompleteLimit);
  }

  public displayTarget(negotiationType: FilteredTarget): string {
    return negotiationType?.name && negotiationType?.id ? `${negotiationType.name} (${negotiationType.id})` : '';
  }

  public trackByTargetId(index: number, target: Target): string {
    return target?.id;
  }

  private getTargetAutocompleteOptions(label: string): Target[] {
    return label ? this._filter(label) : this.targetList.slice(0, this.targetAutocompleteLimit);
  }

  private getCategoryTypeList() {
    this.isCategoryTypeLoading = true;

    this.categoryTypeService
      .getCategoryTypeList({
        case: 'cgrp_off',
      })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        categoryType => {
          this.categoryTypeList = categoryType;
          this.isCategoryTypeLoading = false;

          if (this.categoryTypeList.length > 0 && this.data.cgrpOffData && this.data.cgrpOffData.conditions.length > 0) {
            const preselectedItem = this.categoryTypeList.find(item => item.id === this.data.cgrpOffData.conditions[0].categoryType.id);
            if (preselectedItem) {
              this.categoryTypeFormControl.setValue(preselectedItem);
            }
          }
        },
        error => {
          this.isCategoryTypeLoading = false;
        }
      );
  }

  private handleCategoryTypeValueChange() {
    this.categoryTypeFormControl.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      this.categoryType = value;
      this.prepareItemListLoading(value);
      this.getCategoryItemList(value);
    });
  }

  private prepareItemListLoading(categoryType: CategoryType | null): void {
    this.loadingCategoryTypeId = categoryType?.id ?? null;
    this.itemList = [];
    this.filteredItemList = of([]);
    this.filteredOptions = of([]);
    this.searchFormControl.setValue('', { emitEvent: false });

    if (categoryType) {
      this.isItemLoading = true;
      this.searchFormControl.disable({ emitEvent: false });
    } else {
      this.isItemLoading = false;
      this.searchFormControl.enable({ emitEvent: false });
    }
  }

  private getParamsByCategoryType(categoryType) {
    switch (categoryType.id) {
      case 1: // Advertiser
      case 4: // Product
        return { advertiserGroupId: this.advertiserId };
      case 6: // Campaign
        return { advertiserGroupId: this.advertiserId, year: this.contractYear };
      case 3: // Secodip
        return { year: this.contractYear };
      case 2: // Mandatory / Agency
      case 5: // Product Type
      case 7: // Target
      case 8: // Channel
      case 9: // Screen Type
      case 10: // Screen
      case 11: // Time Slot
      default:
        return {};
    }
  }

  private getCategoryItemList(
    categoryType: CategoryType | null,
    afterLoad: (() => void) | null = null,
    afterError: (() => void) | null = null
  ) {
    if (!categoryType) return;

    const categoryTypeId = categoryType.id;
    let params = this.getParamsByCategoryType(categoryType);

    this.contractCategoryTypeService
      .getItemList(params, categoryType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        items => {
          if (this.loadingCategoryTypeId !== categoryTypeId) return;

          this.isItemLoading = false;
          this.searchFormControl.enable({ emitEvent: false });
          this.itemList = items;
          // this.searchFormControl.enable();
          this.itemList = items;
          /* if (this.data.cgrpOffData && this.data.cgrpOffData.conditions.length > 0) {
            const preselectedItem = this.itemList.find(item => item.id === this.data.cgrpOffData.conditions[0].idCategoryValue);
            if (preselectedItem) {
              this.searchFormControl.setValue(preselectedItem);
            }
          } */
          this.initFilteredItemList();
          this.initFilteredOptions();
          afterLoad?.();
        },
        error => {
          if (this.loadingCategoryTypeId !== categoryTypeId) return;

          this.isItemLoading = false;
          // this.searchFormControl.enable();
          this.searchFormControl.enable({ emitEvent: false });
          afterError?.();
        }
      );
  }

  private initFilteredItemList() {
    if (!this.isItemLoading) {
      this.filteredItemList = this.searchFormControl.valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getItemLabel(value)),
        map(label => this.getItemAutocompleteOptions(label))
      );
    }
  }

  private initFilteredOptions() {
    if (!this.isItemLoading) {
      this.filteredOptions = this.optionFormControl.valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getItemLabel(value)),
        map(label => this.getItemAutocompleteOptions(label))
      );
    }
  }

  /**
   * @param value
   * @private
   */
  private _getItemLabel(value: string | NegotiationType): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  /***
   * @param value
   * @private
   */
  private _itemFilter(value: string = ''): any[] {
    const filterValue = value.toLowerCase();
    return this.itemList
      .filter(item => item.id?.toString().includes(filterValue) || item.name?.toLowerCase().includes(filterValue))
      .slice(0, this.itemAutocompleteLimit);
  }

  public displayItem(item: FilteredItem): string {
    return item?.name && item?.id ? `${item.name} (${item.id})` : '';
  }

  public trackByItemId(index: number, item: any): any {
    return item?.id;
  }

  private getItemAutocompleteOptions(label: string): any[] {
    return label ? this._itemFilter(label) : this.itemList.slice(0, this.itemAutocompleteLimit);
  }

  public addCategoryBloc(conditionType: string): void {
    this.noCategoryBlockAdded = false;
    this.categoryConditionType = conditionType;
  }

  public isFilteredItemListFill(): boolean {
    return this.isItemLoading || !this.categoryTypeFormControl.value || !this.filteredItemList;
  }

  public listAllOptions(): void {
    if (this.isSelectItemsDialogOpen || this.isFilteredItemListFill()) return;

    this.isSelectItemsDialogOpen = true;
    const currentCategoryType = this.categoryTypeFormControl.value;
    this.itemAutocompleteTrigger?.closePanel();

    const dialogRef = this.dialog.open(TvSelectItemsDialogComponent, {
      data: {
        itemList: of(this.itemList),
        itemListAlreadySelected: Array.from(this.selectedOptions).filter(
          (selectedItem: any) => selectedItem.categoryType?.id === currentCategoryType?.id
        ),
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(items => {
        this.isSelectItemsDialogOpen = false;
        if (!items) return;

        Array.from(this.selectedOptions).forEach((selectedItem: any) => {
          if (selectedItem.categoryType?.id === currentCategoryType?.id) {
            this.selectedOptions.delete(selectedItem);
          }
        });

        items.forEach(item => {
          this.selectedOptions.add({
            categoryType: currentCategoryType,
            isInclude: this.categoryConditionType == 'Inclus' ? 1 : 0,
            category: item,
          });
        });

        this.sortSelectedOptions();
      });
  }

  onValidationButtonClick() {
    if (this.saving || this.isLoading) return;

    if (this.targetControl?.valid && this.itemsFilterForm?.valid && this.budgetControl?.valid) {
      let conditions = [];

      if (this.selectedOptions.size > 0) {
        for (const option of this.selectedOptions) {
          conditions.push(option);
        }
      }

      let id;
      if (this.data.action !== 'dup') {
        id = this.data?.cgrpOffData?.id ? this.data.cgrpOffData.id : null;
      }

      let data = {
        id: id,
        contractItemId: this.data.contractItem?.id || null,
        targetCode: this.targetControl && this.targetControl.value ? this.targetControl.value.id : '',
        startDate: this.itemsFilterForm.get('startDate').value,
        endDate: this.itemsFilterForm.get('endDate').value,
        value: this.budgetControl.value,
        clientId: this.data.contract.id,
        stateContractYear: this.data.contract.stateContractYear,
        conditions: conditions,
      };

      this.saving = true;
      const previousDisableClose = this.dialogRef.disableClose;
      this.dialogRef.disableClose = true;
      this.saveStarted.emit();
      let itemSaved = false;

      this.contractCgrpOffService.saveItem(data)
        .pipe(
          take(1),
          switchMap(() => {
            itemSaved = true;
            return this.updateContractCgvModel();
          }),
          finalize(() => {
            this.saving = false;
            this.dialogRef.disableClose = previousDisableClose;
            this.dialogRef.close({ action: 'reload' });
          })
        )
        .subscribe(
          () => this.displaySnackBar('Enregistrement terminé.', 'tv-chip-success'),
          error => {
            const msg = itemSaved
              ? "Erreur lors de la sauvegarde dans l'ancien modèle. Veuillez contacter le support."
              : error?.error?.data?.msg || 'Échec de l’enregistrement.';
            this.displaySnackBar(msg, 'tv-chip-error');
          }
        );
    } else {
      this.displaySnackBar('Veuillez remplir ou corriger le formulaire.', 'tv-chip-error');
    }
  }

  public displaySnackBar(message: string, panelClass: string) {
    this.snackBar.open(message, null, {
      duration: 4000,
      verticalPosition: 'top',
      panelClass: [panelClass],
    });
  }

  private updateContractCgvModel(): Observable<unknown> {
    return this.contractCgvModelUpdateService
      .updateContractCgvModel(this.data.contractItem.id, this.data.contractItem)
      .pipe(take(1));
  }

  onCancelUpdateButtonClick() {
    if (this.saving) return;
    if (this.data.action !== 'delete') {
      this.displaySnackBar("L'ajout / modification a été annulée.", 'tv-chip-warning');
    }
    this.dialogRef.close({
      action: 'cancel',
    });
  }

  okDeleteButtonClick() {
    this.dialogRef.close({
      action: 'delete',
    });
  }

  goBack() {
    this.isShowOption = false;
  }

  saveOptions() {
    this.isShowOption = false;
  }

  onOptionChange(event: any, item: any) {
    if (event.checked) {
      if (!this.isOptionSelected(item)) {
        this.selectedOptions.add({
          categoryType: this.categoryTypeFormControl.value,
          isInclude: this.categoryConditionType == 'Inclus' ? 1 : 0,
          category: item,
        });

        this.sortSelectedOptions();
      }
    } else {
      // this.selectedOptions.delete(item);
      for (const option of this.selectedOptions) {
        if (option.category.id == item.id) {
          this.selectedOptions.delete(option);
          break;
        }
      }
      this.sortSelectedOptions();
    }
  }

  isOptionSelected(item) {
    for (const option of this.selectedOptions) {
      if (option.category.id == item.id) {
        return true;
      }
    }
    return false;
  }

  onSelectAllChange(event: any, item: any) {
    this.selectAllOptions = event.checked;
    this.filteredOptions?.pipe(take(1)).subscribe(options => {
      options.forEach(item => {
        if (this.selectAllOptions) {
          // check existed before adding
          if (!this.isOptionSelected(item)) {
            this.selectedOptions.add(item);
          }
        } else if (!this.selectAllOptions) {
          for (const option of this.selectedOptions) {
            if (option.category.id == item.id) {
              this.selectedOptions.delete(option);
              break;
            }
          }
        }
      });

      this.sortSelectedOptions();
    });
  }

  removeCondition(item: NonNullable<ContractCgrpOff['conditions']>[number]): void {
    this.selectedOptions.delete(item);
    this.sortSelectedOptions();
  }

  isItemExist(itemList: Set<any>, item): boolean {
    if (itemList.size > 0) {
      for (const elem of itemList) {
        if (elem.id == item.id) {
          return true;
        }
      }
    }

    return false;
  }

  private sortSelectedOptions(): void {
    this.selectedOptions = new Set(
      Array.from(this.selectedOptions).sort((a, b) => {
        const typeCompare = (a.categoryType?.name || '').localeCompare(b.categoryType?.name || '', 'fr', {
          sensitivity: 'base',
        });

        if (typeCompare !== 0) {
          return typeCompare;
        }

        return (a.category?.name || '').localeCompare(b.category?.name || '', 'fr', {
          sensitivity: 'base',
        });
      })
    );
    this.conditionRows = Array.from(this.selectedOptions);
  }
}
